import { useState, useEffect } from "react";
import { useFeedback } from "../components/ActionFeedback";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "../layout/no_data.json";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchIcon from "@mui/icons-material/Search";

/* ── Avatar with gradient initials ─────────────────────── */
function AvatarInitials({ name, size = "md" }) {
  const initials = name
    ? name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const palettes = [
    "from-violet-500 to-purple-600",
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-teal-600",
    "from-amber-400 to-orange-500",
    "from-pink-500 to-rose-500",
  ];
  const idx = name ? name.charCodeAt(0) % palettes.length : 0;
  const sizeClass = size === "lg" ? "w-14 h-14 text-lg" : "w-11 h-11 text-sm";
  return (
    <div
      className={`${sizeClass} rounded-xl bg-gradient-to-br ${palettes[idx]} flex items-center justify-center text-white font-black shadow-md flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════ */
export default function Users() {
  const { showToast, showModal, closeModal } = useFeedback();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await API.get("/auth/users");
      setUsers(res.data.filter((u) => u.role !== "admin"));
    } catch {
      showToast("Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ── Add user modal ─────────────────────────────────── */
  const handleAddUser = () => {
    showModal({
      title: "Create New User",
      subtitle: "User will be saved to the database",
      content: (
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            const data = {
              name: fd.get("name"),
              email: fd.get("email"),
              password: fd.get("password"),
              role: fd.get("role"),
            };
            try {
              await API.post("/auth/users", data);
              showToast("User created and saved successfully.", "success");
              fetchUsers();
              closeModal();
            } catch (err) {
              const msg = err.response?.data?.message || "Failed to create user.";
              showToast(msg, "error");
            }
          }}
        >
          {[
            { label: "Full Name", name: "name", type: "text", placeholder: "e.g. John Doe" },
            { label: "Email Address", name: "email", type: "email", placeholder: "user@example.com" },
            { label: "Password", name: "password", type: "password", placeholder: "Min. 6 characters" },
          ].map((f) => (
            <div key={f.name} className="space-y-1.5">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
                {f.label}
              </label>
              <input
                name={f.name}
                type={f.type}
                placeholder={f.placeholder}
                minLength={f.name === "password" ? 6 : undefined}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-[#0a0c10]/10 transition-all"
                required
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">
              System Role
            </label>
            <select
              name="role"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-[#0a0c10]/10"
            >
              <option value="user">App User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full bg-[#0a0c10] text-white px-8 py-4 rounded-xl text-xs font-black shadow-xl hover:bg-slate-800 transition-all uppercase tracking-widest mt-4"
          >
            Create User
          </button>
        </form>
      ),
    });
  };

  /* ── User detail modal ──────────────────────────────── */
  const handleUserClick = (user) => {
    const isActive = !user.status || user.status === "Active";
    showModal({
      title: "User Profile",
      subtitle: "Account details & access control",
      content: (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-5 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-100">
            <AvatarInitials name={user.name} size="lg" />
            <div>
              <p className="text-lg font-black text-slate-800 tracking-tight">{user.name}</p>
              <p className="text-xs font-bold text-slate-400 mt-0.5">{user.email}</p>
              <span
                className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                  user.role === "admin"
                    ? "bg-[#0a0c10] text-white border-[#0a0c10]"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {user.role === "admin" ? "Admin" : "User"}
              </span>
            </div>
          </div>
          {/* Details */}
          <div className="rounded-xl border border-slate-100 overflow-hidden divide-y divide-slate-50">
            {[
              { label: "Email", value: user.email },
              {
                label: "Member Since",
                value: new Date(user.createdAt).toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                }),
              },
              { label: "Status", value: user.status || "Active" },
            ].map((row) => (
              <div key={row.label} className="flex justify-between items-center px-5 py-3.5 bg-slate-50/50">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{row.label}</span>
                <span className="text-sm font-black text-slate-800">{row.value}</span>
              </div>
            ))}
            {/* Toggle */}
            <div className="flex justify-between items-center px-5 py-3.5 bg-slate-50/50">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Portal Access</span>
              <div
                onClick={async () => {
                  try {
                    const newStatus = isActive ? "Offline" : "Active";
                    await API.put(`/auth/users/${user._id}`, { status: newStatus });
                    showToast(`Status changed to ${newStatus}.`, "success");
                    fetchUsers();
                    closeModal();
                  } catch {
                    showToast("Failed to update user status.", "error");
                  }
                }}
                className={`w-12 h-6 ${isActive ? "bg-emerald-400" : "bg-slate-200"} rounded-full relative cursor-pointer transition-colors shadow-inner`}
              >
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-sm ${isActive ? "right-1" : "left-1"}`} />
              </div>
            </div>
          </div>
        </div>
      ),
    });
  };

  /* ── Derived stats ──────────────────────────────────── */
  const total = users.length;
  const activeCount = users.filter((u) => !u.status || u.status === "Active").length;
  const offlineCount = users.filter((u) => u.status === "Offline").length;

  /* ── Filtered rows ──────────────────────────────────── */
  const filtered = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      (u.name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term);
    const matchRole =
      roleFilter === "all" ||
      (roleFilter === "active" ? !u.status || u.status === "Active" : u.status === "Offline");
    return matchSearch && matchRole;
  });

  /* ── Animation variants ─────────────────────────────── */
  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={container}
      className="space-y-10"
    >
      {/* ── Page header ─────────────────────────────────── */}
      <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 px-1">
        <div>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Management</p>
          <h2 className="text-white text-4xl font-black tracking-tighter leading-none">
            System Users
          </h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleAddUser}
          className="flex items-center gap-2 bg-white text-[#0a0c10] px-6 py-3 rounded-2xl text-xs font-black shadow-lg transition-all uppercase tracking-widest hover:bg-slate-100"
        >
          <PersonAddAltIcon fontSize="small" /> Add Member
        </motion.button>
      </motion.div>

      {/* ── Stat strip — puzzle-card-dark ───────────────── */}
      <motion.div variants={item} className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Users", value: total, icon: <GroupOutlinedIcon fontSize="small" />, accent: "text-white", sub: "registered" },
          { label: "Active", value: activeCount, icon: <CheckCircleOutlinedIcon fontSize="small" />, accent: "text-emerald-400", sub: "online" },
          { label: "Offline", value: offlineCount, icon: <DoNotDisturbOnOutlinedIcon fontSize="small" />, accent: "text-slate-400", sub: "inactive" },
        ].map((s) => (
          <div key={s.label} className="puzzle-card-dark flex flex-col gap-3 relative overflow-hidden group">
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all duration-700 pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className={`${s.accent}`}>{s.icon}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">{s.sub}</span>
            </div>
            <p className={`text-4xl font-black tracking-tighter ${s.accent}`}>
              {loading ? "—" : s.value.toLocaleString()}
            </p>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* ── Table card — puzzle-card-light ──────────────── */}
      <motion.div variants={item} className="puzzle-card-light !p-0 overflow-hidden shadow-2xl border-none">

        {/* Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" style={{ fontSize: 18 }} />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3.5 px-5 pl-12 text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0a0c10]/10 transition-all"
            />
          </div>
          {/* Filter pills */}
          <div className="flex gap-2">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "offline", label: "Offline" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setRoleFilter(f.key)}
                className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                  roleFilter === f.key
                    ? "bg-[#0a0c10] text-white border-[#0a0c10] shadow-lg"
                    : "bg-slate-50 text-slate-500 border-slate-100 hover:bg-slate-100 hover:text-slate-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table body */}
        {loading ? (
          <div className="py-28 flex flex-col items-center gap-5">
            <div className="w-9 h-9 border-4 border-slate-100 border-t-[#0a0c10] rounded-full animate-spin" />
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Fetching users…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center">
            <div className="w-44 h-44 mb-4 opacity-60">
              <Lottie animationData={noDataAnimation} loop />
            </div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">No users found</p>
            {(searchTerm || roleFilter !== "all") && (
              <button
                onClick={() => { setSearchTerm(""); setRoleFilter("all"); }}
                className="mt-4 text-xs font-bold text-[#0a0c10] underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["User", "Role", "Status", "Member Since"].map((h) => (
                    <th key={h} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <AnimatePresence>
                <motion.tbody initial="hidden" animate="visible" variants={container}>
                  {filtered.map((user) => {
                    const isActive = !user.status || user.status === "Active";
                    return (
                      <motion.tr
                        key={user._id}
                        variants={item}
                        onClick={() => handleUserClick(user)}
                        className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors cursor-pointer group"
                      >
                        {/* User */}
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <AvatarInitials name={user.name} />
                            <div>
                              <p className="font-black text-slate-800 text-sm tracking-tight group-hover:text-[#0a0c10] transition-colors">
                                {user.name}
                              </p>
                              <p className="text-[11px] text-slate-400 font-bold mt-0.5">{user.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Role */}
                        <td className="px-8 py-5">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                              user.role === "admin"
                                ? "bg-[#0a0c10] text-white border-[#0a0c10]"
                                : "bg-slate-100 text-slate-600 border-slate-200"
                            }`}
                          >
                            {user.role === "admin" ? "Admin" : "User"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-8 py-5">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                isActive ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" : "bg-slate-300"
                              }`}
                            />
                            <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest">
                              {user.status || "Active"}
                            </span>
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-8 py-5">
                          <p className="text-[11px] font-bold text-slate-500 whitespace-nowrap">
                            {new Date(user.createdAt).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}
                          </p>
                        </td>
                      </motion.tr>
                    );
                  })}
                </motion.tbody>
              </AnimatePresence>
            </table>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-slate-50 flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Showing {filtered.length} of {total} users
              </p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
